import { WeatherParameterForm } from "@/overlay/components/WeatherParameterForm/index";
import { weatherParamOptionFilter } from "@/overlay/components/WeatherParameterForm/optionFilter";
import { BasicSearchResultList } from "@/overlay/components/WeatherParameterSearchForm/BasicSearchResultList";
import { BasicWeatherParameterSearchInput } from "@/overlay/components/WeatherParameterSearchForm/BasicWeatherParameterSearchInput";
import { classNames } from "@/utility/jsx";
import { searchEnginesWeatherParams } from "@/weather-parameters";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import type { CrossModelParameterSpecification } from "@mm/api-layers.meteomatics.com";
import Logger from "@mm/logging";
import {
  type ExactMatchResult,
  type HasLayerIdxNarrowing,
  type PartialWeatherParameter,
  type SearchResults,
  autofill,
} from "@mm/weather-parameter-utils";
import React, { useMemo, useState } from "react";
import { Button, ButtonBar } from "../CommonParts/buttons";
import styles from "./style.module.scss";

export type PlotParameterType = "parameter" | "windbarb" | "wavebarb" | "weathersymbol";

export type AddParameterFormProps = {
  onSubmitParam: (param: HasLayerIdxNarrowing<PartialWeatherParameter>, parameterType: PlotParameterType) => void;
  onCancel: () => void;
  source: "add-param" | "edit-param" | "add-plot-param" | "add-table-param";
};

/**
 * An inline form to add or edit weather parameters.
 */
export function AddParameterForm(props: AddParameterFormProps) {
  const logger = Logger.fromFilename(__filename);

  // State for the current parameter being edited or added
  const [currParam, setCurrParam] = useState<HasLayerIdxNarrowing<PartialWeatherParameter> | null>();

  // Determine available search engines
  const searchEngineOptions = [{ label: "Standard", searchEngine: searchEnginesWeatherParams.standard }];
  const [searchResults, setSearchResults] = useState<SearchResults | null>(null);

  // State for the type of parameter
  const [parameterType, setParameterType] = useState<PlotParameterType>("parameter");

  // Handler for submitting the parameter
  function handleSubmitParam() {
    if (props.onSubmitParam && currParam) {
      props.onSubmitParam(autofill(currParam), parameterType);
      // Reset form after submission
      setCurrParam(null);
      setParameterType("parameter");
    } else {
      logger.error("Parameter submission failed. Either onSubmitParam is not defined or currParam is null.");
    }
  }

  function onSelectLayerGroup(result: CrossModelParameterSpecification) {
    const param = autofill({
      // Remove unavailable options for this layer kind.
      parameter: {
        ...result,
        layers: result.layers.map((layer) => weatherParamOptionFilter(layer, "PlotParameter")),
      },
    });
    setSearchResults(null);
    setCurrParam(param);
  }

  function onSelectExactParam(result: ExactMatchResult) {
    const param = autofill({
      parameter: result.layerGroup,
      narrowed_selection: {
        layerIdx: result.matchingLayers[0].layerIdx,
        // biome-ignore lint/style/noNonNullAssertion: TODO a lot of places
        fields: result.matchingLayers[0].match.groups!,
      },
    });
    setSearchResults(null);
    setCurrParam(param);
  }

  function onChangeParamOptions(param: HasLayerIdxNarrowing<PartialWeatherParameter>) {
    setCurrParam(param);
  }

  return (
    <div className={styles["add-parameter-form"]}>
      <h2>{props.source.includes("edit") ? t`Edit Layer` : t`Add Layer`}</h2>

      {/* Render the appropriate form based on parameterType */}
      {parameterType === "parameter" && (
        <>
          <div className={classNames(styles["search-input-wrapper"])}>
            <BasicWeatherParameterSearchInput
              searchEngine={searchEngineOptions[0].searchEngine}
              onChange={setSearchResults}
              onSelectExactParamMatch={onSelectExactParam}
            />
          </div>
          {searchResults && (
            <div className={styles["search-result-wrapper"]}>
              <BasicSearchResultList
                results={searchResults}
                onSelectLayerGroup={onSelectLayerGroup}
                onSelectExactParamMatch={onSelectExactParam}
              />
            </div>
          )}
          {currParam && (
            <WeatherParameterForm
              showExactParameter={true}
              withTitle={true}
              autofill={false}
              parameter={currParam}
              onChange={onChangeParamOptions}
            />
          )}
        </>
      )}

      {/* Action Buttons */}
      <div className={classNames(styles["action-buttons"])}>
        <ButtonBar align="right">
          <Button type="button" onClick={props.onCancel}>
            <Trans>Cancel</Trans>
          </Button>
          {currParam ? (
            <Button type="button" onClick={handleSubmitParam}>
              <Trans>Apply</Trans>
            </Button>
          ) : (
            <Button type="button" disabled>
              <Trans>Apply</Trans>
            </Button>
          )}
        </ButtonBar>
      </div>
    </div>
  );
}
